import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/c849dba7-0dd0-4750-80bd-42c89faa40b3/images/6a18271b999fad536c36cdc913b734fbd11eaf7440fed13d2b483e3807367a06.png';

const GRAD_TEXT = {
  background: 'linear-gradient(135deg, #F67425 0%, #BB0518 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

// Static animation configs (module-level to avoid re-created inline objects)
const GREETING_ANIM = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: 'easeOut' },
};
const NUMBER_ANIM = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.7, ease: 'easeOut' },
};
const TEASER_ANIM = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
};
const SCROLL_BOB = {
  animate: { y: [0, 8, 0] },
  transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
};

const SECTION_STYLE = {
  position: 'relative', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  padding: '12vh 1.5rem 4.5rem', minHeight: '560px',
};
const OVERLINE_STYLE = {
  fontFamily: 'var(--font-body)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.25em',
  textTransform: 'uppercase', color: '#F67425', display: 'block', marginBottom: '1.5rem',
};
const HEADING_STYLE = {
  fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900,
  letterSpacing: '-0.04em', lineHeight: 1.05, color: '#302B2C', marginBottom: '1.5rem',
};
const NUMBER_CARD_STYLE = {
  display: 'inline-flex', alignItems: 'baseline', gap: '0.5rem', padding: '0.75rem 2rem',
  background: '#fff', border: '1px solid rgba(48, 43, 44, 0.1)', borderRadius: '2px',
  boxShadow: '0 12px 32px -10px rgba(246,116,37,0.2)',
};
const BIG_NUMBER_STYLE = {
  fontFamily: 'var(--font-heading)', fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 900, lineHeight: 1, ...GRAD_TEXT,
};
const NUMBER_LABEL_STYLE = {
  fontFamily: 'var(--font-body)', fontSize: 'clamp(1rem, 2vw, 1.25rem)', color: 'rgba(48,43,44,0.72)', fontWeight: 500,
};
const TEASER_TEXT_STYLE = {
  fontFamily: 'var(--font-body)', fontSize: '1.0625rem', color: 'rgba(48,43,44,0.72)',
  lineHeight: 1.7, maxWidth: '520px', margin: '0 auto 3rem',
};
const SCROLL_BTN_STYLE = {
  background: '#fff', border: '1px solid rgba(246,116,37,0.35)', borderRadius: '50%',
  width: '48px', height: '48px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#F67425', boxShadow: '0 0 0 4px rgba(246,116,37,0.1)',
  transition: 'border-color 0.3s, color 0.3s, box-shadow 0.3s',
};

const CountUp = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (end <= 0) return;
    let current = 0;
    const stepMs = Math.max(1, Math.floor(duration / end));
    const timer = setInterval(() => {
      current += 1;
      setCount(current);
      if (current >= end) clearInterval(timer);
    }, stepMs);
    return () => clearInterval(timer);
  }, [end, duration]);
  return <>{count}</>;
};

const GreetingPhase = ({ firstName }) => (
  <motion.div {...GREETING_ANIM}>
    <span style={OVERLINE_STYLE}>Assessment Complete</span>
    <h1 style={HEADING_STYLE}>
      {firstName}, your world<br />
      <span style={GRAD_TEXT}>just got bigger.</span>
    </h1>
  </motion.div>
);

const BigNumberPhase = ({ totalCountries }) => (
  <motion.div {...NUMBER_ANIM} style={{ marginBottom: '2rem' }}>
    <div style={NUMBER_CARD_STYLE} data-pdf-statcard="1">
      <span style={BIG_NUMBER_STYLE}>
        <CountUp end={totalCountries} duration={1500} />
      </span>
      <span style={NUMBER_LABEL_STYLE}>countries where your profile is strongest</span>
    </div>
  </motion.div>
);

const TopMatchTeaser = ({ topCountry, topFitLabel, onScrollDown }) => (
  <motion.div {...TEASER_ANIM}>
    <p style={TEASER_TEXT_STYLE}>
      Your strongest match is <strong style={{ color: '#302B2C', fontWeight: 700 }}>{topCountry}</strong> — rated{' '}
      <span style={{
        color: topFitLabel === 'Likely Fit' ? '#0B815A' : topFitLabel === 'Possible Fit' ? '#0B815A' : '#D97706',
        fontWeight: 700,
      }}>
        {topFitLabel}
      </span>
      . Scroll to explore your full migration map.
    </p>

    <motion.button
      data-testid="hero-scroll-down-btn"
      onClick={onScrollDown}
      {...SCROLL_BOB}
      style={SCROLL_BTN_STYLE}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#F67425'; e.currentTarget.style.boxShadow = '0 0 0 6px rgba(246,116,37,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(246,116,37,0.35)'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(246,116,37,0.1)'; }}
    >
      <ChevronDown size={20} />
    </motion.button>
  </motion.div>
);

const HeroReveal = ({ userName, totalCountries, topCountry, topFitLabel, onScrollDown }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 400),
      setTimeout(() => setPhase(2), 1200),
      setTimeout(() => setPhase(3), 2200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const firstName = userName?.split(' ')[0] || 'there';

  return (
    <section data-testid="hero-reveal-section" style={SECTION_STYLE}>
      {/* Background image */}
      <div className="ken-burns" style={{
        position: 'absolute', inset: '-4%', zIndex: 0,
        backgroundImage: `url(${HERO_BG})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
      }} />
      {/* White gradient overlay for legibility */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: 'linear-gradient(to bottom, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.78) 50%, rgba(255,255,255,0.96) 100%)',
      }} />

      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: '720px' }}>
        <AnimatePresence>{phase >= 1 && <GreetingPhase firstName={firstName} />}</AnimatePresence>
        <AnimatePresence>{phase >= 2 && <BigNumberPhase totalCountries={totalCountries} />}</AnimatePresence>
        <AnimatePresence>
          {phase >= 3 && <TopMatchTeaser topCountry={topCountry} topFitLabel={topFitLabel} onScrollDown={onScrollDown} />}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HeroReveal;
